<template>
    <HeaderCardsLayout variant="No-Logo">
        <template #headline>{{ $t('scenarios') }}</template>
        <template #icon>
            <BrainIcon class="size-12" />
        </template>
        <template #buttons>
            <LinkButton
                variant="Info-Strong"
                size="Base"
                :disabled-explanation="createScenarioGroupDisabledExplanation"
                :to="getCreateScenarioGroupLink()"
            >
                <template #icon>
                    <PlusIcon class="size-5" />
                </template>
                {{ $t('scenario-group-create') }}
            </LinkButton>
        </template>
        <template #body>
            <PaddedContent>
                <div v-if="loading" class="space-y-4">
                    <Message status="info">{{ $t('loading-scenario-groups') }}</Message>
                </div>
                <div v-if="!loading && scenarioGroups.length === 0" class="space-y-4">
                    <EmptyState>
                        <template #description>
                            {{ $t('no-scenario-groups') }}
                        </template>
                        <template #call-to-action>
                            <LinkButton size="Base" variant="Info-Strong" :to="getCreateScenarioGroupLink()">
                                <template #icon>
                                    <PlusIcon class="size-5" />
                                </template>
                                {{ $t('scenario-group-create') }}
                            </LinkButton>
                        </template>
                    </EmptyState>
                </div>
                <div v-if="!loading && scenarioGroups.length > 0" class="space-y-4">
                    <CollapsibleHeadline
                        v-for="[status, statusGroups] of groupStates"
                        :key="status"
                        :heading-level="2"
                        :initial-is-open="status === 'active'"
                    >
                        <template #headline
                            >{{ getScenarioGroupStatusLabel(status) }} ({{ statusGroups.length }})</template
                        >
                        <template #content>
                            <div v-if="statusGroups && statusGroups.length > 0" class="space-y-4">
                                <HeadlessCard
                                    v-for="scenarioGroup in statusGroups"
                                    :key="scenarioGroup.id"
                                    :invisible-headline="scenarioGroup.nameShort"
                                >
                                    <div class="grid grid-cols-[1fr_min-content]">
                                        <CardRowLinkWithSubtitle
                                            :link-text="scenarioGroup.nameLong"
                                            :link-target="linkToScenarioGroup(scenarioGroup.id)"
                                            :has-border-bottom="true"
                                        >
                                            <template #additional-content>
                                                <ScenarioGroupIndex :index="scenarioGroup.index" :max-digits="5" />
                                                <span class="text-gray-400 font-light">|</span>
                                            </template>
                                            <template #subtitle>
                                                <template v-if="scenarioGroup.scenarios.totalCount > 0">
                                                    <i18n
                                                        path="scenario-group-subtitle-styled"
                                                        class="text-gray-400 font-light"
                                                        tag="div"
                                                        :args="{ count: scenarioGroup.scenarios.totalCount }"
                                                    >
                                                        <template #scenariosTotalCount>
                                                            <span class="text-gray-700 font-bold">
                                                                {{ scenarioGroup.scenarios.totalCount }}
                                                            </span>
                                                        </template>
                                                        <template #scenariosYearString>
                                                            <template
                                                                v-for="(element, index) in getScenarioGroupYears(
                                                                    scenarioGroup.id,
                                                                )"
                                                                :key="index"
                                                            >
                                                                <span class="text-gray-600 font-semibold">
                                                                    <template v-if="element.length === 1">
                                                                        {{ element[0] }}
                                                                    </template>
                                                                    <template v-if="element.length === 2">
                                                                        {{ element[0] }}
                                                                        <span class="text-gray-400">-</span>
                                                                        {{ element[1] }}
                                                                    </template>
                                                                </span>
                                                                <span
                                                                    v-if="
                                                                        index <
                                                                        getScenarioGroupYears(scenarioGroup.id).length -
                                                                            1
                                                                    "
                                                                    class="text-gray-400 font-light pl-px"
                                                                >
                                                                    |
                                                                </span>
                                                            </template>
                                                        </template>
                                                    </i18n>
                                                </template>
                                                <div v-else class="text-gray-400 font-light">
                                                    {{ $t('no-scenarios-yet') }}
                                                </div>
                                            </template>
                                        </CardRowLinkWithSubtitle>
                                        <div class="flex flex-col self-start p-2 justify-self-end">
                                            <Badge size="Extra-Small" :variant="getScenarioGroupBadgeVariant(status)">{{
                                                getScenarioGroupStatusLabel(status)
                                            }}</Badge>
                                            <div></div>
                                        </div>
                                    </div>
                                </HeadlessCard>
                            </div>
                            <div v-else class="space-y-4 font-light text-gray-600">
                                ({{ $t('no-scenario-groups-with-this-status') }})
                            </div>
                        </template>
                    </CollapsibleHeadline>
                </div>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import Badge from '@/components/Badge/Badge.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import CardRowLinkWithSubtitle from '@/components/Card/CardRowLinkWithSubtitle.vue';
import HeadlessCard from '@/components/Card/HeadlessCard.vue';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
import BrainIcon from '@/components/Icon/BrainIcon.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import Message from '@/components/Message/Message.vue';
import ScenarioGroupIndex from '@/components/ScenarioGroupIndex/ScenarioGroupIndex.vue';
import { GetScenarioGroupsDocument, ScenarioGroupDetailsFragment } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import {
    getCreateScenarioGroupLink,
    getScenarioGroupBadgeVariant,
    getScenarioGroupStatusLabel,
    linkToScenarioGroup,
} from '../scenario';
import { scenarioGroupStates, ScenarioGroupStatus } from '../types';

const isAdmin = auth.isAdmin();
const fluent = useFluent();

const { result: getScenarioGroups, loading } = useQuery(GetScenarioGroupsDocument);
const scenarioGroups = computed(() => getScenarioGroups.value?.scenarioGroups?.nodes ?? []);
const groupStates = computed(() => {
    const groupedObjects = Object.groupBy(scenarioGroups.value, ({ status }) => status);
    // add map with all states, fill with empty array, if no grouped objects exist
    const stateMap: Map<ScenarioGroupStatus, Array<ScenarioGroupDetailsFragment>> = new Map();
    for (const status of scenarioGroupStates) {
        stateMap.set(status, groupedObjects[status] ?? []);
    }
    return stateMap;
});

const createScenarioGroupDisabledExplanation = computed(() => {
    if (!isAdmin) return fluent.$t('no-permission-to-edit');
});

function formatYearList(years: string[]) {
    const result: Array<string[]> = [];
    let startYear = years[0];
    let endYear = years[0];

    for (let i = 1; i < years.length; i++) {
        if (Number(years[i]) === Number(endYear) + 1) {
            endYear = years[i];
        } else {
            if (startYear === endYear) {
                result.push([startYear]);
            } else {
                result.push([startYear, endYear]);
            }
            startYear = years[i];
            endYear = years[i];
        }
    }

    if (startYear === endYear) {
        result.push([startYear]);
    } else {
        result.push([startYear, endYear]);
    }

    return result;
}

function getScenarioGroupYears(id: string) {
    const group = scenarioGroups.value.find((g) => g.id === id);
    if (group && group.scenarios.totalCount > 0) {
        const scenarios = group.scenarios.nodes;
        const scenarioYears = Object.groupBy(scenarios, ({ year }) => year.toString());
        const years = Object.keys(scenarioYears);
        return formatYearList(years);
    }
    return [];
}

useHeadSafe({
    title: () => `${fluent.$t('scenarios')}`,
});
</script>
